import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Palette, Loader2, Upload, X, Brush, Eraser, Download, LayoutTemplate, Wand2, RotateCcw, Sparkles } from "lucide-react";

const FINISHES = [
  { id: "auto", label: "Auto" },
  { id: "matte", label: "Matte" },
  { id: "glossy", label: "Glossy" },
  { id: "metallic", label: "Metallic" },
  { id: "plastic", label: "Plastic" },
  { id: "fabric", label: "Fabric" },
];
const PRESET_COLORS = ["#DB960D", "#232323", "#FFFFFF", "#C0392B", "#2C6E49", "#1F4E8C", "#7D3C98", "#E8B4B8"];

export default function ImageRecolor() {
  const navigate = useNavigate();
  const imgRef = useRef(null);
  const maskRef = useRef(null);
  const fileRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const srcData = useRef(null);

  const [imgSrc, setImgSrc] = useState(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [brush, setBrush] = useState(40);
  const [mode, setMode] = useState("brush"); // brush | erase | wand
  const [tolerance, setTolerance] = useState(45);
  const [painted, setPainted] = useState(false);
  const [color, setColor] = useState("#DB960D");
  const [finish, setFinish] = useState("auto");
  const [part, setPart] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [brandColors, setBrandColors] = useState([]);

  useEffect(() => { api.get("/brandkit").then((r) => setBrandColors(r.data?.colors || [])).catch(() => {}); }, []);

  // resize mask canvas whenever display dims change, clear it, and cache source pixels for the wand
  useEffect(() => {
    const c = maskRef.current;
    if (!c || !dims.w) return;
    c.width = dims.w; c.height = dims.h;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setPainted(false);
    const img = imgRef.current;
    if (img && img.complete) {
      const off = document.createElement("canvas");
      off.width = dims.w; off.height = dims.h;
      const octx = off.getContext("2d", { willReadFrequently: true });
      octx.drawImage(img, 0, 0, dims.w, dims.h);
      try { srcData.current = octx.getImageData(0, 0, dims.w, dims.h); } catch (_) { srcData.current = null; }
    }
  }, [dims, imgSrc]);

  const onUpload = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Upload an image file"); return; }
    const reader = new FileReader();
    reader.onload = () => { setResult(null); setImgSrc(reader.result); };
    reader.readAsDataURL(f);
  };
  const onDrop = (e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { const r = new FileReader(); r.onload = () => { setResult(null); setImgSrc(r.result); }; r.readAsDataURL(f); } };

  const onImgLoad = (e) => {
    const n = e.target;
    setNatural({ w: n.naturalWidth, h: n.naturalHeight });
    const maxW = 560, maxH = 520;
    const scale = Math.min(1, maxW / n.naturalWidth, maxH / n.naturalHeight);
    setDims({ w: Math.round(n.naturalWidth * scale), h: Math.round(n.naturalHeight * scale) });
  };

  const posOf = (e) => {
    const c = maskRef.current, r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const stroke = (a, b) => {
    const ctx = maskRef.current.getContext("2d");
    ctx.lineJoin = ctx.lineCap = "round"; ctx.lineWidth = brush;
    ctx.globalCompositeOperation = mode === "erase" ? "destination-out" : "source-over";
    ctx.strokeStyle = "rgba(219,150,13,0.55)";
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  };
  // Magic-wand: click a point → flood-fill the connected region of similar colour into the mask
  const wandSelect = (e) => {
    const c = maskRef.current, src = srcData.current;
    if (!imgSrc || !src) return;
    const r = c.getBoundingClientRect();
    const W = c.width, H = c.height;
    const x = Math.round((e.clientX - r.left) * (W / r.width));
    const y = Math.round((e.clientY - r.top) * (H / r.height));
    if (x < 0 || y < 0 || x >= W || y >= H) return;
    const sp = src.data;
    const seed = (y * W + x) * 4;
    const sr = sp[seed], sg = sp[seed + 1], sb = sp[seed + 2];
    const tol = tolerance;
    const ctx = c.getContext("2d");
    const md = ctx.getImageData(0, 0, W, H);
    const m = md.data;
    const visited = new Uint8Array(W * H);
    const stack = [y * W + x];
    visited[y * W + x] = 1;
    let count = 0;
    while (stack.length) {
      const p = stack.pop();
      const q = p * 4;
      if (Math.abs(sp[q] - sr) + Math.abs(sp[q + 1] - sg) + Math.abs(sp[q + 2] - sb) > tol) continue;
      m[q] = 219; m[q + 1] = 150; m[q + 2] = 13; m[q + 3] = 140;
      count++;
      const px = p % W, py = (p / W) | 0;
      if (px > 0 && !visited[p - 1]) { visited[p - 1] = 1; stack.push(p - 1); }
      if (px < W - 1 && !visited[p + 1]) { visited[p + 1] = 1; stack.push(p + 1); }
      if (py > 0 && !visited[p - W]) { visited[p - W] = 1; stack.push(p - W); }
      if (py < H - 1 && !visited[p + W]) { visited[p + W] = 1; stack.push(p + W); }
    }
    ctx.putImageData(md, 0, 0);
    if (count > 0) setPainted(true);
  };
  const down = (e) => {
    if (!imgSrc) return;
    if (mode === "wand") { wandSelect(e); return; }
    drawing.current = true; const p = posOf(e); last.current = p; stroke(p, { x: p.x + 0.01, y: p.y }); if (mode !== "erase") setPainted(true);
  };
  const move = (e) => { if (!drawing.current) return; const p = posOf(e); stroke(last.current, p); last.current = p; };
  const up = () => { drawing.current = false; last.current = null; };
  const clearMask = () => { const c = maskRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setPainted(false); };

  const generate = async () => {
    if (!imgSrc) { toast.error("Upload a product photo first"); return; }
    if (!painted && !part.trim()) { toast.error("Paint the area to recolor, or describe it in the text field"); return; }
    setBusy(true); setResult(null);
    try {
      const mask = painted ? maskRef.current.toDataURL("image/png") : null;
      const { data } = await api.post("/ai/recolor", { image: imgSrc, mask, color, part: part.trim() || null, finish, count: 1 });
      setResult(data.image);
      toast.success("Recolored");
    } catch (e) { toast.error(e.response?.data?.detail || "Recolor failed"); }
    finally { setBusy(false); }
  };

  const download = (src) => { const a = document.createElement("a"); a.href = src; a.download = "kann-recolor.png"; a.click(); };
  const sendToDesign = async (src) => {
    const w = natural.w || 1080, h = natural.h || 1080;
    try {
      const { data } = await api.post("/designs", { name: "Recolored photo", width: w, height: h, background: "#FFFFFF",
        elements: [{ id: "recolor-img", type: "image", src, x: 0, y: 0, width: w, height: h, objectFit: "cover", borderRadius: 0, opacity: 1 }] });
      navigate(`/editor/${data.id}`);
    } catch (e) { toast.error("Could not create design"); }
  };

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-16 md:pt-10 relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 620, height: 620, top: -140, left: "55%" }} /></div>

        <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">Powered by Google Nano Banana Pro</div>
        <h1 className="text-3xl sm:text-4xl font-medium text-kann-text-pri mt-2">Recolor Studio</h1>
        <p className="font-light text-kann-text-sec mt-2 max-w-2xl">Upload a product photo, select the part you want to change with the <span className="text-kann-text-pri font-medium">Toverstaf</span> (click a shape — it auto-detects the region) or the brush, pick a colour — and get a photorealistic recolor with the original texture, shine and shadows kept.</p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Left: image + brush */}
          <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">1 · Mark the area</span>
              {imgSrc && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setMode("wand")} data-testid="wand-tool" className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 border transition-colors ${mode === "wand" ? "bg-kann-gold text-kann-ink border-kann-gold" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><Sparkles className="w-3.5 h-3.5" /> Toverstaf</button>
                  <button onClick={() => setMode("brush")} data-testid="brush-tool" className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 border transition-colors ${mode === "brush" ? "bg-kann-gold text-kann-ink border-kann-gold" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><Brush className="w-3.5 h-3.5" /> Brush</button>
                  <button onClick={() => setMode("erase")} data-testid="erase-tool" className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 border transition-colors ${mode === "erase" ? "bg-kann-gold text-kann-ink border-kann-gold" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}><Eraser className="w-3.5 h-3.5" /> Erase</button>
                  <button onClick={clearMask} data-testid="clear-mask" className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1 border border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"><RotateCcw className="w-3.5 h-3.5" /> Clear</button>
                </div>
              )}
            </div>

            {!imgSrc ? (
              <button onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={onDrop} data-testid="recolor-dropzone"
                className="w-full border border-dashed border-kann-border/50 rounded-lg py-20 flex flex-col items-center justify-center gap-3 text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors">
                <div className="w-16 h-16 rounded-full bg-kann-base border border-kann-border/40 flex items-center justify-center"><Upload className="w-7 h-7" /></div>
                <div className="text-sm font-medium">Drop a product photo here or click to upload</div>
              </button>
            ) : (
              <div className="flex flex-col items-center">
                <div className="relative rounded-md overflow-hidden border border-kann-border/30 touch-none" style={{ width: dims.w, height: dims.h, maxWidth: "100%" }}>
                  <img ref={imgRef} src={imgSrc} alt="upload" onLoad={onImgLoad} className="block select-none pointer-events-none" style={{ width: dims.w, height: dims.h }} draggable={false} />
                  <canvas ref={maskRef} data-testid="mask-canvas"
                    className="absolute inset-0 cursor-crosshair"
                    style={{ width: dims.w, height: dims.h, touchAction: "none" }}
                    onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
                </div>
                <div className="w-full mt-3 flex items-center gap-3">
                  {mode === "wand" ? (
                    <>
                      <span className="text-[11px] uppercase tracking-wide text-kann-text-sec font-mono whitespace-nowrap">Tolerantie</span>
                      <input type="range" min="10" max="150" value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} data-testid="wand-tolerance" className="flex-1 accent-kann-gold" />
                    </>
                  ) : (
                    <>
                      <span className="text-[11px] uppercase tracking-wide text-kann-text-sec font-mono">Brush</span>
                      <input type="range" min="8" max="120" value={brush} onChange={(e) => setBrush(Number(e.target.value))} data-testid="brush-size" className="flex-1 accent-kann-gold" />
                    </>
                  )}
                  <button onClick={() => fileRef.current?.click()} data-testid="recolor-change-image" className="text-xs text-kann-gold hover:underline flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> New photo</button>
                </div>
                <p className="text-[11px] text-kann-text-sec mt-1 self-start">{mode === "wand" ? "Klik op een vlak — de toverstaf selecteert automatisch het aaneengesloten gebied. Klik meerdere vlakken om ze op te tellen; stel de tolerantie bij voor strakkere of ruimere randen." : "Tip: paint over the whole part — the gold overlay just marks it, it won't appear in the result."}</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="recolor-file-input" />
          </div>

          {/* Right: controls + result */}
          <div className="space-y-6">
            <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4 md:p-6 space-y-5">
              <span className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono">2 · Choose the new colour</span>
              <div>
                <div className="flex items-center gap-3">
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} data-testid="recolor-color" className="w-11 h-11 rounded-md border border-kann-border/40 bg-transparent cursor-pointer" />
                  <input value={color} onChange={(e) => setColor(e.target.value)} data-testid="recolor-hex" className="w-32 bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold px-3 py-2 text-sm outline-none" />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[...brandColors, ...PRESET_COLORS.filter((c) => !brandColors.includes(c))].map((c, i) => (
                    <button key={`${c}-${i}`} title={c} onClick={() => setColor(c)} data-testid={`recolor-preset-${i}`}
                      className={`w-7 h-7 rounded-full border transition-transform hover:scale-110 ${color.toUpperCase() === c.toUpperCase() ? "ring-2 ring-kann-gold border-white" : "border-kann-border/40"}`} style={{ background: c }} />
                  ))}
                </div>
                {brandColors.length > 0 && <p className="text-[11px] text-kann-text-sec mt-2">Your Brand Kit colours are shown first.</p>}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wide text-kann-text-sec font-mono">Finish</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {FINISHES.map((f) => (
                    <button key={f.id} onClick={() => setFinish(f.id)} data-testid={`finish-${f.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${finish === f.id ? "bg-kann-ink text-white font-semibold" : "border border-kann-border/40 text-kann-text-sec hover:bg-kann-surface-active"}`}>{f.label}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wide text-kann-text-sec font-mono">Describe the part (optional)</label>
                <input value={part} onChange={(e) => setPart(e.target.value)} data-testid="recolor-part" placeholder="e.g. the cap, the left cushion, the sole"
                  className="mt-2 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold px-3 py-2 text-sm outline-none" />
                <p className="text-[11px] text-kann-text-sec mt-1">Use the brush for precision, the text for context, or both.</p>
              </div>

              <button onClick={generate} disabled={busy || !imgSrc} data-testid="recolor-generate-btn"
                className="w-full bg-kann-ink text-white font-semibold rounded-full py-3 text-sm hover:bg-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {busy ? "Recoloring…" : "Recolor photo"}
              </button>
            </div>

            <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4 md:p-6 flex flex-col">
              <span className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono mb-3">Result</span>
              <div className="flex-1 flex items-center justify-center bg-kann-base rounded-md border border-kann-border/30 overflow-hidden" style={{ minHeight: 240 }} data-testid="recolor-result">
                {busy ? (
                  <div className="text-center text-kann-text-sec"><Loader2 className="w-7 h-7 animate-spin mx-auto mb-2 text-kann-gold" />Recoloring your photo…</div>
                ) : result ? (
                  <img src={result} alt="recolored" data-testid="recolor-result-image" className="max-w-full max-h-[420px] object-contain" />
                ) : (
                  <span className="text-kann-text-sec text-sm px-6 text-center">Your recolored photo will appear here.</span>
                )}
              </div>
              {result && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => sendToDesign(result)} data-testid="recolor-use-btn" className="flex-1 bg-kann-gold text-kann-ink font-semibold rounded-full py-2.5 text-sm hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2"><LayoutTemplate className="w-4 h-4" /> Use in design</button>
                  <button onClick={() => download(result)} data-testid="recolor-download-btn" className="border border-kann-border/50 rounded-full px-4 py-2.5 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2"><Download className="w-4 h-4" /> Download</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
